/**
 * Procedural felt MARKINGS texture — drawn to a 2D canvas and used as a
 * transparent overlay laid flat on the felt. Painting into a CanvasTexture keeps
 * the table cheap and needs ZERO external assets, so it always renders.
 *
 * ORIENTATION (audited): the overlay plane is a `planeGeometry(width, depth)`
 * rotated -PI/2 about X and placed at world (0, topY, czMid). With CanvasTexture
 * flipY (default):
 *   • image TOP    → world -z  → the FLAT (dealer) edge / notch,
 *   • image BOTTOM → world +z  → the CURVED (player) edge / betting boxes,
 *   • image +x     → world +x.
 * A player stands on the +z (curved) side looking toward -z; in that reading
 * frame world +x is to their right and world -z is "up/away", so PLAIN canvas
 * text (no rotation) already reads UPRIGHT to the player, and the arc text only
 * adds a small per-glyph tangent tilt. The plane + canvas now share the FELT's
 * own aspect ratio (wide & shallow), so the layout fills the surface instead of
 * being squished into a central strip.
 *
 * Everything drawn is CONTAINED inside the green felt with a clear margin (see the
 * numerical check in the repo notes). It draws, like a real casino table:
 *   • 11 rounded-rectangle BETTING BOXES fanned along the player arc,
 *   • arc text concentric with the dealer arc: "BLACKJACK PAYS 3 TO 2" over
 *     "DEALER MUST DRAW TO 16 AND STAND ON ALL 17S", then "INSURANCE PAYS 2 TO 1"
 *     with "2 TO 1" at each end.
 *
 * Positions match `seating.ts` exactly (same D parameterisation + insets), so a
 * blob/chip/card lands on its painted box.
 */

import * as THREE from 'three';
import {
  TABLE,
  FLAT_Z,
  PLAYER_SEAT_COUNT,
  BET_CIRCLE_R,
  SEAT_MARGIN,
  ARC_INWARD,
  playerArcPoint,
} from './seating';

/** World margin around the felt bbox that the overlay plane/canvas covers. */
const MARGIN = 0.18;

/**
 * The overlay plane in world units, matching the FELT bounding box (+margin) so
 * the texture is NOT stretched and the markings fill the surface. The felt spans
 * x ∈ [-radiusX, radiusX] and z ∈ [FLAT_Z, FLAT_Z + radiusZ].
 */
export const MARKINGS_PLANE = {
  width: 2 * (TABLE.radiusX + MARGIN),
  depth: TABLE.radiusZ + 2 * MARGIN,
  /** World z of the plane center (felt bbox center). */
  centerZ: FLAT_Z + TABLE.radiusZ / 2,
} as const;

/** Canvas pixels — aspect matched to the plane so x/y scale equally (no stretch). */
const TEX_LONG = 1536; // along the wide (x) axis
const TEXW = TEX_LONG;
const TEXH = Math.round((TEX_LONG * MARKINGS_PLANE.depth) / MARKINGS_PLANE.width);

/**
 * Inward WORLD offsets (meters) from the felt's player edge for the painted text
 * rows, deepening toward the dealer. These are concentric offsets of the SAME
 * player curve the table geometry uses (via `playerArcPoint`), so the drawing
 * nests inside the table's curved edge with the correct concavity. The betting
 * boxes + card ticks reuse the shared `ARC_INWARD` so they coincide with the seat
 * anchors. (Verified by the numerical containment/concavity check.)
 */
const TEXT_INWARD = {
  headline: 1.45,
  rule: 1.85,
  /** Deepest row but kept clear of the (now-deeper) dealer notch back at z≈-0.98. */
  insurance: 2.1,
  /** End "2 TO 1" caps — shallower so they stay clear of the dealer notch. */
  endCap: 1.3,
} as const;

/**
 * Map a world (x, z) on the felt to canvas pixel coords.
 *
 * Derivation: plane is `planeGeometry(W, D)` rotated -PI/2 about X at center
 * (0, topY, czMid). UV.u (0..1) → local_x → world_x = (u-0.5)*W. UV.v → local_y →
 * world_z = czMid - (v-0.5)*D (rotation maps +y → -z). CanvasTexture flipY makes
 * image py=0 (top) ↔ v=1. Inverting:
 *   u    = world_x / W + 0.5
 *   v    = 0.5 - (world_z - czMid) / D
 *   px   = u  * TEXW
 *   py   = (1 - v) * TEXH
 */
function worldToPx(x: number, z: number): [number, number] {
  const u = x / MARKINGS_PLANE.width + 0.5;
  const v = 0.5 - (z - MARKINGS_PLANE.centerZ) / MARKINGS_PLANE.depth;
  return [u * TEXW, (1 - v) * TEXH];
}

/**
 * World length → canvas pixels. The plane/canvas are aspect-matched, so the x and
 * z pixel scales are equal; use the x scale.
 */
function lenToPx(len: number): number {
  return (len / MARKINGS_PLANE.width) * TEXW;
}

const GOLD = '#e7c466';
const GOLD_SOFT = 'rgba(231, 196, 102, 0.6)';
const CREAM = 'rgba(244, 233, 201, 0.92)';
const SCARLET = '#e8b54a'; // warm amber for the insurance line (reads on green)
const HEADLINE = '#ff7a3c'; // red/orange casino headline

/**
 * Draw text following the player arc at a constant inward WORLD offset, concentric
 * with the table's curved player edge (via the shared `playerArcPoint`) and
 * upright from the player viewpoint. Characters are placed along the arc
 * (parameter t, left→right) and given a small per-glyph tilt tangent to the arc.
 * `tCenter` is the arc midpoint; `arcSpan` the total t-width of the text;
 * `inward` is the world-meter offset toward the dealer/interior.
 */
function drawArcText(
  ctx: CanvasRenderingContext2D,
  text: string,
  inward: number,
  tCenter: number,
  arcSpan: number,
  fontPx: number,
  color: string,
  bold: boolean,
): void {
  ctx.save();
  ctx.fillStyle = color;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.font = `${bold ? 'bold ' : ''}${Math.round(fontPx)}px Georgia, "Times New Roman", serif`;
  const n = text.length;
  for (let i = 0; i < n; i++) {
    const frac = n === 1 ? 0.5 : i / (n - 1);
    // t increases left→right; in canvas that is +px, so glyphs read in order.
    const t = tCenter - arcSpan / 2 + frac * arcSpan;
    const [wx, wz] = playerArcPoint(t, inward);
    const [px, py] = worldToPx(wx, wz);
    // Tangent in canvas space (sample a hair along increasing t).
    const [wx2, wz2] = playerArcPoint(t + 0.001, inward);
    const [px2, py2] = worldToPx(wx2, wz2);
    const ang = Math.atan2(py2 - py, px2 - px);
    ctx.save();
    ctx.translate(px, py);
    ctx.rotate(ang);
    ctx.fillText(text[i]!, 0, 0);
    ctx.restore();
  }
  ctx.restore();
}

/** Draw a rounded-rectangle path (betting box). */
function roundRect(ctx: CanvasRenderingContext2D, cx: number, cy: number, w: number, h: number, r: number): void {
  const x = cx - w / 2;
  const y = cy - h / 2;
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.arcTo(x + w, y, x + w, y + r, r);
  ctx.lineTo(x + w, y + h - r);
  ctx.arcTo(x + w, y + h, x + w - r, y + h, r);
  ctx.lineTo(x + r, y + h);
  ctx.arcTo(x, y + h, x, y + h - r, r);
  ctx.lineTo(x, y + r);
  ctx.arcTo(x, y, x + r, y, r);
  ctx.closePath();
}

/**
 * Build the markings CanvasTexture. Returns the texture (sRGB, transparent
 * background). Caller disposes it on unmount.
 */
export function buildFeltMarkings(): THREE.CanvasTexture {
  const canvas = document.createElement('canvas');
  canvas.width = TEXW;
  canvas.height = TEXH;
  const ctx = canvas.getContext('2d');
  if (!ctx) return new THREE.CanvasTexture(canvas);

  ctx.clearRect(0, 0, TEXW, TEXH);
  ctx.lineJoin = 'round';
  ctx.lineCap = 'round';

  // ---- Arc text concentric with the player edge (deepening toward the dealer) ----
  // Closest to the boxes: the headline.
  drawArcText(ctx, 'BLACKJACK PAYS 3 TO 2', TEXT_INWARD.headline, 0.5, 0.5, lenToPx(0.22), HEADLINE, true);
  // Rule line just behind the headline.
  drawArcText(
    ctx,
    'DEALER MUST DRAW TO 16 AND STAND ON ALL 17S',
    TEXT_INWARD.rule,
    0.5,
    0.56,
    lenToPx(0.1),
    CREAM,
    false,
  );
  // Insurance line on the deepest arc (nearest the dealer), kept across the middle
  // so it never reaches the dealer notch.
  drawArcText(ctx, 'INSURANCE PAYS 2 TO 1', TEXT_INWARD.insurance, 0.5, 0.34, lenToPx(0.13), SCARLET, true);
  // "2 TO 1" near each END of the arc, on a SHALLOWER offset (toward the player
  // edge) so the end caps stay clear of the dealer notch.
  drawArcText(ctx, '2 TO 1', TEXT_INWARD.endCap, 0.13, 0.09, lenToPx(0.1), GOLD_SOFT, true);
  drawArcText(ctx, '2 TO 1', TEXT_INWARD.endCap, 0.87, 0.09, lenToPx(0.1), GOLD_SOFT, true);

  // ---- 11 betting BOXES (rounded rectangles), fanned along the player arc ----
  const count: number = PLAYER_SEAT_COUNT;
  const margin = SEAT_MARGIN;
  const spanT = 1 - margin * 2;
  const boxW = lenToPx(BET_CIRCLE_R * 1.7);
  const boxH = lenToPx(BET_CIRCLE_R * 1.45);
  for (let i = 0; i < count; i++) {
    const t = count === 1 ? 0.5 : margin + (i / (count - 1)) * spanT;

    // Concentric inward offset of the player edge — same as seat.betCircle.
    const [bx, bz] = playerArcPoint(t, ARC_INWARD.box);
    const [bpx, bpy] = worldToPx(bx, bz);

    // Outer box.
    ctx.strokeStyle = GOLD;
    ctx.lineWidth = lenToPx(0.022);
    roundRect(ctx, bpx, bpy, boxW, boxH, lenToPx(0.06));
    ctx.stroke();
    // Inner faint box.
    ctx.strokeStyle = GOLD_SOFT;
    ctx.lineWidth = lenToPx(0.01);
    roundRect(ctx, bpx, bpy, boxW * 0.78, boxH * 0.7, lenToPx(0.04));
    ctx.stroke();

    // Seat number centered in the box.
    ctx.fillStyle = GOLD_SOFT;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.font = `bold ${Math.round(lenToPx(0.1))}px Georgia, serif`;
    ctx.fillText(String(i + 1), bpx, bpy);

    // NOTE: the white "card-slot" tick that used to be painted just above each box
    // was removed (it looked confusing). Cards still LAND at seat.cardAnchor
    // (ARC_INWARD.card in seating.ts) — that anchor is unchanged; only the visible
    // felt paint is gone.
  }

  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 4;
  tex.needsUpdate = true;
  return tex;
}
